import { createI18n } from 'vue-i18n'
import sentryConfig from 'src/store/sentry.js'
import fallbackMessages from '@sdui-hq/app-internationalization/locales/de.json'
import fallbackCopyright from '@sdui-hq/app-internationalization/privacy-statements/de.md?raw'
import {
  fallbackLocale,
  fallbackLocalePrivacy,
  languages,
  privacyStatementsLanguages,
  defaultLocale,
  supportedLocales,
} from 'src/config/language_helpers.ts'
// eslint-disable-next-line import/extensions
import { clone } from 'bootstrap-vue/esm/utils/object'
import { marked } from 'marked'
import type { SupportedCodes } from 'src/config/language_helpers.ts'
import type { I18nOptions } from 'vue-i18n'

type MessageSchema = typeof fallbackMessages;
const messages: Record<SupportedCodes, MessageSchema> = supportedLocales.reduce(
  (acc, locale) => {
    acc[locale] =
      locale === defaultLocale
        ? ({ ...(fallbackMessages as any), privacy_statement: normalizeVueI18nMarkdown(marked.parse(fallbackCopyright) as string) } as MessageSchema)
        : ({} as MessageSchema)

    return acc
  },
  {} as Record<SupportedCodes, MessageSchema>,
)

const loadedLanguages = [fallbackLocale]
const dateFormats = {
  de: {
    short: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    },
    long: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
    },
  },
  en: {
    short: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    },
    long: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
    },
  },
} as const

const options: I18nOptions = {
  /*
  TODO VUE 3: Goal: set legacy to "false" (and drop it with vue-i18n v12)
  Because we have so many vue files with options API, we must keep this "true" for now.
  This is a workaround for the fact that vue-i18n does not support the options API in Vue 3 (unless with set this to true)

  This will enable to still be able to use this.$t or this.$tm in options API components.
  Long term goal: All files should be converted to composition API, then we can set this to false.

  Caveat:
  Because of this, in composition API components, we must use `const { t } = useI18n({ useScope: 'global' })`, instead of just `const { t } = useI18n()`.
  Basically telling vue-i18n to use the global scope, so that we can access the global i18n instance.
  So later, when we set legacy is set to "false", we can remove the `useScope: 'global'` and replace all this.$t and this.$tm with useI18n().
  */
  legacy: true,
  warnHtmlInMessage: 'off', // disable warning HTML in message
  silentFallbackWarn: true, // disable warning for silent fallback
  locale: fallbackLocale,
  fallbackLocale,
  messages,
  datetimeFormats: dateFormats,
  // TODO JHE Enable this again. It was just disabled to reduce the number of Sentry errors temporarily.
  // send missing translations to sentry
  // missing: (locale, key) => {
  //   const whitelist = ['variables.DEFAULT.DEFAULT'] // 'Sdui'
  //   if (whitelist.includes(key)) return
  //   sentryConfig.throwError(`I18N Error: Cannot translate the value of keypath '${key}' for locale '${locale}'.`)
  // },
}

// init vue i18n instance
export const i18n = createI18n<false, typeof options>(options)

// i18n lazy loading
function setI18nLanguage(lang: SupportedCodes): SupportedCodes {
  // TODO VUE 3: vee-i18n add `.value` and remove @ts-ignore at the end of when legacy API is removed
  // @ts-expect-error: vue-i18n legacy mode requires direct assignment to locale property
  i18n.global.locale = lang
  document.querySelector('html')?.setAttribute('lang', lang)
  return lang
}

export function getDictionary() {
  // TODO VUE 3: vee-i18n add `.value` and remove @ts-ignore at the end of when legacy API is removed
  // @ts-expect-error: i18n.global.messages is not typed as Record<SupportedCodes, { validations?: unknown }>
  const messages = i18n.global.messages as Record<SupportedCodes, { validations?: unknown }>

  return (Object.keys(languages) as SupportedCodes[]).reduce(
    (acc, key) => {
      return {
        ...acc,
        [key]: clone(messages[key]?.validations),
      }
    },
    {} as Record<SupportedCodes, unknown>,
  )
}

export async function loadLanguageAsync(lang: SupportedCodes) {
  // If the same language
  // TODO VUE 3: vee-i18n add `.value` and remove @ts-ignore at the end of when legacy API is removed
  // @ts-expect-error: i18n.global.locale is not typed as SupportedCodes
  if (i18n.global.locale === lang) return Promise.resolve(setI18nLanguage(lang))

  // If the language was already loaded
  if (loadedLanguages.includes(lang)) return Promise.resolve(setI18nLanguage(lang))

  // If the language hasn't been loaded yet
  const messages = await import(`../../node_modules/@sdui-hq/app-internationalization/locales/${lang}.json`)

  i18n.global.setLocaleMessage(lang, messages.default)
  loadedLanguages.push(lang)
  setI18nLanguage(lang)

  const matchedPrivacyStatementsLanguage = privacyStatementsLanguages.includes(lang)
  const privacyStatementsLanguage = matchedPrivacyStatementsLanguage ? lang : fallbackLocalePrivacy

  try {
    const loadPrivacy = async (locale: string) => {
      // TODO Use import.meta.glob pattern when languages are moved into FE repo
      const markdownUrl = new URL(`../../node_modules/@sdui-hq/app-internationalization/privacy-statements/${locale}.md`, import.meta.url).href
      const res = await fetch(markdownUrl)
      if (!res.ok) throw new Error(`Could not load privacy statement for ${locale}`)
      return await res.text()
    }

    const copyright = await loadPrivacy(privacyStatementsLanguage)

    // TODO VUE 3: vee-i18n add `.value` and remove @ts-ignore at the end of when legacy API is removed
    // @ts-expect-error: i18n.global.mergeLocaleMessage is not typed as (locale: SupportedCodes, messages: Record<string, unknown>) => void
    i18n.global.mergeLocaleMessage(i18n.global.locale, { privacy_statement: normalizeVueI18nMarkdown(marked.parse(copyright)) })
  } catch (error) {
    console.error(error)
    sentryConfig.addBreadcrumb({
      category: 'exception',
      message: error instanceof Error ? error.message : String(error),
      level: 'error',
    })
    sentryConfig.throwError(`error-when-trying-to-load-async-copyright: ${privacyStatementsLanguage}`)
  }
}

// Cache the original `tm` method BEFORE overriding
const originalTm = i18n.global.tm.bind(i18n.global)

// create our own `tm` method to resolve nested keys with interpolation
i18n.global.tm = ((key: string) => {
  return tmWithInterpolation(key)
}) as typeof i18n.global.tm

export const tmWithInterpolation = (key: string): Record<string, any> | any[] | string => {
  // Use cached original `tm` to prevent infinite recursion
  const result = originalTm(key)

  if ((typeof result !== 'object' && !Array.isArray(result)) || result === null) {
    const error = {
      message: `I18N Error: '${key}' does not contain an object or array.`,
    }

    console.error(error)
    sentryConfig.throwError(error)

    throw new Error(error.message)
  }

  return recursivelyRefreshLangKeys(result, key)
}

function recursivelyRefreshLangKeys(obj: Record<string, any>, path: string): Record<string, any> | any[] | string {
  if (Array.isArray(obj)) {
    return obj.map((value, index) => {
      const newPath = `${path}.${index}`
      return recursivelyRefreshLangKeys(value, newPath)
    })
  }

  if (typeof obj === 'object' && obj !== null) {
    const res: [string, unknown][] = Object.entries(obj).map(([key, value]) => {
      const newPath = `${path}.${key}`
      return [key, recursivelyRefreshLangKeys(value, newPath)]
    })

    return Object.fromEntries(res)
  }

  return i18n.global.t(path)
}

// this is needed to normalize the markdown for vue-i18n
function normalizeVueI18nMarkdown(html: string): string {
  const textarea = document.createElement('textarea')
  textarea.innerHTML = html
  const decoded = textarea.value
  return decoded.replace(/@:\{(.*?)\}/g, '@:{$1}')
}
